import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../api";
import { dayShort, num, pct, rupee, rupeeShort, signClass } from "../format";
import type { Rule, Trade, Tradebook } from "../types";
import { RULE_CATEGORIES } from "../types";

interface Props {
  /** The selected book's trades, already loaded by the app. */
  trades: Trade[];
  rules: Rule[];
  book: Tradebook | null;
  books: Tradebook[];
}

/**
 * Every figure here is computed from the trades in the browser — no LLM, no
 * server round trip, no estimation.
 *
 * Anomalous trades are excluded throughout. A position opened before the
 * imported window has no cost basis, and a corporate action that hasn't been
 * confirmed will book a fabricated loss — including either would quietly
 * poison every statistic on this page.
 */
export function Summary({ trades, rules, book, books }: Props) {
  const [allBooks, setAllBooks] = useState(false);
  const [combined, setCombined] = useState<Trade[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Fetched per book rather than by relaxing /api/trades: that endpoint
  // requires a book on purpose, and two requests is a small price for not
  // opening a door that blends accounts by omission.
  useEffect(() => {
    if (!allBooks) return;
    let cancelled = false;
    setError(null);
    Promise.all(books.map((b) => api.trades({ tradebook_id: b.id })))
      .then((lists) => !cancelled && setCombined(lists.flat()))
      .catch((e) => !cancelled && setError((e as Error).message));
    return () => {
      cancelled = true;
    };
  }, [allBooks, books]);

  // Memoised: the `?? []` would otherwise be a fresh array every render, and
  // every statistic on the page recomputes off this.
  const scoped = useMemo(
    () => (allBooks ? (combined ?? []) : trades),
    [allBooks, combined, trades],
  );
  // Percentages need the capital actually behind these trades.
  const capital = allBooks
    ? books.reduce((a, b) => a + b.capital_base, 0)
    : (book?.capital_base ?? 0);

  const stats = useMemo(() => {
    const closed = scoped.filter((t) => t.status === "closed" && t.net_pnl !== null);
    const trusted = closed.filter((t) => !t.anomaly);
    const excluded = closed.length - trusted.length;

    const wins = trusted.filter((t) => (t.net_pnl ?? 0) > 0);
    const losses = trusted.filter((t) => (t.net_pnl ?? 0) < 0);

    const sum = (rows: Trade[]) => rows.reduce((acc, t) => acc + (t.net_pnl ?? 0), 0);
    const netPnl = sum(trusted);
    const grossWin = sum(wins);
    const grossLoss = Math.abs(sum(losses));

    const avgWin = wins.length ? grossWin / wins.length : 0;
    const avgLoss = losses.length ? grossLoss / losses.length : 0;
    const winRate = trusted.length ? (wins.length / trusted.length) * 100 : 0;

    // Expectancy: what an average trade is worth, in rupees.
    const expectancy = trusted.length ? netPnl / trusted.length : 0;
    const profitFactor = grossLoss > 0 ? grossWin / grossLoss : null;

    // Max drawdown along the realized equity curve, ordered by exit.
    const byExit = [...trusted].sort((a, b) =>
      (a.exit_date ?? "").localeCompare(b.exit_date ?? ""),
    );
    let running = 0;
    let peak = 0;
    let maxDd = 0;
    const curve: { date: string; equity: number }[] = [];
    for (const t of byExit) {
      running += t.net_pnl ?? 0;
      peak = Math.max(peak, running);
      maxDd = Math.min(maxDd, running - peak);
      curve.push({ date: t.exit_date ?? "", equity: running });
    }

    const holds = trusted.filter((t) => t.holding_days);
    const avgHold = holds.length
      ? holds.reduce((a, t) => a + (t.holding_days ?? 0), 0) / holds.length
      : 0;

    const charges = trusted.reduce((a, t) => a + (t.charges ?? 0), 0);

    return {
      closed,
      trusted,
      excluded,
      wins,
      losses,
      netPnl,
      avgWin,
      avgLoss,
      winRate,
      expectancy,
      profitFactor,
      maxDd,
      avgHold,
      charges,
      curve,
      open: scoped.filter((t) => t.status === "open").length,
    };
  }, [scoped]);

  // Per-rule win rate: the whole point of ticking the checklists.
  const ruleStats = useMemo(() => {
    const trusted = stats.trusted;
    return rules
      .map((rule) => {
        const withRule = trusted.filter((t) => t.journal?.rule_ids.includes(rule.id));
        const without = trusted.filter((t) => !t.journal?.rule_ids.includes(rule.id));
        const rate = (rows: Trade[]) =>
          rows.length ? (rows.filter((t) => (t.net_pnl ?? 0) > 0).length / rows.length) * 100 : null;
        return {
          rule,
          n: withRule.length,
          withRate: rate(withRule),
          withoutRate: rate(without),
          avgPnl: withRule.length
            ? withRule.reduce((a, t) => a + (t.net_pnl ?? 0), 0) / withRule.length
            : null,
        };
      })
      .filter((r) => r.n > 0);
  }, [rules, stats.trusted]);

  const untagged = stats.trusted.filter((t) => !t.journal?.strategy).length;

  // Declared before the empty state: a book with no closed trades must still
  // offer the switch to the combined view, or it is a dead end.
  const scopePicker = (
    <>
      {books.length > 1 && (
        <div className="toolbar">
          <div className="field">
            <label>Books</label>
            <select
              value={allBooks ? "all" : "one"}
              onChange={(e) => setAllBooks(e.target.value === "all")}
            >
              <option value="one">{book?.name ?? "Selected book"} only</option>
              <option value="all">All books combined</option>
            </select>
          </div>
          {allBooks && combined === null && <div className="muted">Loading every book…</div>}
        </div>
      )}
      {error && <div className="banner error">{error}</div>}
    </>
  );

  if (stats.closed.length === 0) {
    return (
      <>
        {scopePicker}
        <div className="card empty">
          <h3>Nothing to summarise yet</h3>
          <p className="muted">Close a trade and its statistics will appear here.</p>
        </div>
      </>
    );
  }

  return (
    <>
      {scopePicker}

      {stats.excluded > 0 && (
        <div className="banner warn">
          <strong>
            {stats.excluded} flagged trade{stats.excluded === 1 ? "" : "s"} excluded
          </strong>{" "}
          from every figure below. These have no reliable cost basis — usually a position opened
          before your imported date range, or an unconfirmed corporate action. Including them
          would distort the whole page.
        </div>
      )}

      {untagged > 0 && (
        <div className="banner warn">
          {untagged} closed trade{untagged === 1 ? " has" : "s have"} no strategy tag, so
          {untagged === 1 ? " it is" : " they are"} invisible to every per-strategy breakdown.
        </div>
      )}

      <div className="card">
        <div className="section-title">Performance · {stats.trusted.length} closed trades</div>
        <div className="stat-strip" style={{ border: "none", paddingBottom: 0 }}>
          <S k="Net P&L" v={rupee(stats.netPnl)} cls={signClass(stats.netPnl)} />
          <S k="Win Rate" v={pct(stats.winRate, 1)} />
          <S k="Wins / Losses" v={`${stats.wins.length} / ${stats.losses.length}`} />
          <S k="Avg Win" v={rupee(stats.avgWin)} cls="pos" />
          {/* Negating a zero renders as "-0.00", so only flip an actual loss. */}
          <S k="Avg Loss" v={rupee(stats.avgLoss ? -stats.avgLoss : 0)} cls="neg" />
          <S
            k="Expectancy"
            v={`${rupee(stats.expectancy)}/trade`}
            cls={signClass(stats.expectancy)}
          />
          <S
            k="Profit Factor"
            v={stats.profitFactor === null ? "—" : num(stats.profitFactor)}
          />
          <S k="Max Drawdown" v={rupee(stats.maxDd)} cls="neg" />
          <S k="Avg Hold" v={`${num(stats.avgHold, 1)} days`} />
          <S k="Costs Paid" v={rupee(stats.charges)} cls="neg" />
          <S k="Open Positions" v={String(stats.open)} />
          {capital > 0 && (
            <S
              k="Return on Capital"
              v={pct((stats.netPnl / capital) * 100)}
              cls={signClass(stats.netPnl)}
            />
          )}
        </div>
      </div>

      <div className="card">
        <div className="section-title">Realized equity curve</div>
        <EquityCurve points={stats.curve} />
      </div>

      <ExcursionAnalysis trades={stats.trusted} />

      <div className="card">
        <div className="section-title">Win rate by rule</div>
        {ruleStats.length === 0 ? (
          <div className="hint">
            Tick rules on your closed trades and this table will tell you which parts of your
            process actually pay — and which are superstition.
          </div>
        ) : (
          <table className="legs">
            <thead>
              <tr>
                <th>Rule</th>
                <th>Trades</th>
                <th>Win % when followed</th>
                <th>Win % when not</th>
                <th>Avg P&L</th>
              </tr>
            </thead>
            <tbody>
              {RULE_CATEGORIES.flatMap((cat) => {
                const rows = ruleStats.filter((r) => r.rule.category === cat.key);
                if (rows.length === 0) return [];
                return [
                  <tr key={cat.key}>
                    <td colSpan={5} className="muted" style={{ fontWeight: 700, fontSize: 11 }}>
                      {cat.label.toUpperCase()}
                    </td>
                  </tr>,
                  ...rows.map((r) => {
                    const edge =
                      r.withRate !== null && r.withoutRate !== null
                        ? r.withRate - r.withoutRate
                        : null;
                    return (
                      <tr key={r.rule.id}>
                        <td>{r.rule.text}</td>
                        <td>
                          {r.n}
                          {r.n < 5 && <span className="muted"> (thin)</span>}
                        </td>
                        <td className={edge !== null && edge > 0 ? "pos" : ""}>
                          {pct(r.withRate, 0)}
                        </td>
                        <td className="muted">{pct(r.withoutRate, 0)}</td>
                        <td className={signClass(r.avgPnl)}>{rupee(r.avgPnl)}</td>
                      </tr>
                    );
                  }),
                ];
              })}
            </tbody>
          </table>
        )}
        <div className="hint">
          Rules with fewer than five trades are marked thin — a 100% win rate over two trades is
          noise, not evidence.
        </div>
      </div>
    </>
  );
}

function S({ k, v, cls }: { k: string; v: string; cls?: string }) {
  return (
    <div className="stat">
      <div className="k">{k}</div>
      <div className={`v ${cls ?? ""}`}>{v}</div>
    </div>
  );
}

/** Inline SVG — no chart library needed for a single series. */
/**
 * What MAE and MFE say about how trades were managed.
 *
 * Three questions, each answerable only with excursion data: is the stop in
 * the right place, how much of what was offered gets kept, and were stops
 * honoured.
 */
function ExcursionAnalysis({ trades }: { trades: Trade[] }) {
  const measured = trades.filter((t) => t.mae !== null && t.mfe !== null);
  if (measured.length === 0) return null;

  const winners = measured.filter((t) => (t.net_pnl ?? 0) > 0);
  const losers = measured.filter((t) => (t.net_pnl ?? 0) <= 0);

  // The deepest a winner ever dipped is the honest floor for a stop: anything
  // tighter would have taken you out of a trade that went on to work.
  const worstWinnerDip = winners.length ? Math.min(...winners.map((t) => t.mae ?? 0)) : null;
  const medianWinnerDip = median(winners.map((t) => t.mae ?? 0));
  const medianLoserDip = median(losers.map((t) => t.mae ?? 0));

  const captures = measured
    .filter((t) => (t.mfe ?? 0) > 0 && t.pct_return !== null)
    .map((t) => ((t.pct_return ?? 0) / (t.mfe ?? 1)) * 100);
  const medianCapture = median(captures);

  // A stop is only breached if one was recorded. Silence is not discipline.
  const withStops = measured.filter(
    (t) => t.journal?.sl_price != null && t.avg_entry > 0,
  );
  const breaches = withStops.filter((t) => {
    const stopPct = (((t.journal?.sl_price ?? 0) - t.avg_entry) / t.avg_entry) * 100;
    return (t.mae ?? 0) < stopPct;
  });

  return (
    <div className="card">
      <div className="section-title">
        Trade management · {measured.length} measured trades
      </div>
      <p className="hint" style={{ marginTop: 0 }}>
        MAE is how far a trade went against you while you held it; MFE is the best it
        offered. Trades whose price history is incomplete are left out rather than
        counted as zero.
      </p>

      <div className="stat-strip" style={{ border: "none", paddingBottom: 0 }}>
        <S
          k="Deepest Winner Dip"
          v={worstWinnerDip === null ? "—" : pct(worstWinnerDip, 1)}
          cls="neg"
        />
        <S k="Median Winner Dip" v={medianWinnerDip === null ? "—" : pct(medianWinnerDip, 1)} />
        <S k="Median Loser Dip" v={medianLoserDip === null ? "—" : pct(medianLoserDip, 1)} />
        <S
          k="Median Capture Of MFE"
          v={medianCapture === null ? "—" : pct(medianCapture, 0)}
        />
        <S
          k="Stops Breached"
          v={withStops.length === 0 ? "—" : `${breaches.length} of ${withStops.length}`}
          cls={breaches.length > 0 ? "neg" : undefined}
        />
      </div>

      <div className="hint" style={{ marginTop: 14 }}>
        {worstWinnerDip !== null && (
          <>
            No winning trade went further than <strong>{pct(worstWinnerDip, 1)}</strong>{" "}
            against you, so a stop tighter than that would have cost you one of them.{" "}
          </>
        )}
        {medianCapture !== null && medianCapture < 60 && (
          <>
            You keep about <strong>{pct(medianCapture, 0)}</strong> of what the median
            trade offered — that is an exit question, not an entry one.{" "}
          </>
        )}
        {withStops.length === 0 &&
          "Record a stop on your closed trades and this will also tell you which ones you sat through."}
      </div>
    </div>
  );
}

/** Middle value, or null for an empty set — never zero, which would read as data. */
function median(values: number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

function EquityCurve({ points }: { points: { date: string; equity: number }[] }) {
  // The nearest point to the pointer, or null when it has left the chart.
  const [hover, setHover] = useState<number | null>(null);
  // The chart is drawn at its real pixel width so one viewBox unit is one
  // pixel. A fixed viewBox would scale the axis text with the chart, which on
  // a phone shrinks a 10px label to about four.
  const wrap = useRef<HTMLDivElement>(null);
  const [measured, setMeasured] = useState(900);

  useEffect(() => {
    const element = wrap.current;
    if (!element || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([entry]) =>
      setMeasured(Math.max(260, Math.round(entry.contentRect.width))),
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  if (points.length < 2) {
    return <div className="hint">Close at least two trades to plot a curve.</div>;
  }

  const width = measured;
  const height = 240;
  // Room for the axis labels. Without it the curve had no scale at all —
  // a shape with no numbers says only "up and to the right".
  const left = 58;
  const right = 14;
  const top = 14;
  const bottom = 26;

  const values = points.map((p) => p.equity);
  const min = Math.min(0, ...values);
  const max = Math.max(0, ...values);
  const span = max - min || 1;

  // Laid out by date, not by trade number: equal spacing per trade would
  // claim a quiet month and a busy week took the same time.
  const times = points.map((p) => new Date(`${p.date}T00:00:00`).getTime());
  const first = times[0];
  const lastTime = times[times.length - 1];
  const timeSpan = lastTime - first || 1;

  const x = (i: number) => left + ((times[i] - first) / timeSpan) * (width - left - right);
  const y = (v: number) => height - bottom - ((v - min) / span) * (height - top - bottom);

  const path = points.map((p, i) => `${i === 0 ? "M" : "L"} ${x(i)} ${y(p.equity)}`).join(" ");
  const last = values[values.length - 1];
  const stroke = last >= 0 ? "var(--accent)" : "var(--loss)";

  // Four gridlines is enough to read a level off without becoming a ladder.
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => min + f * span);
  // Three dates: the ends and the middle. More would collide at phone width.
  // Deduplicated because with two or three trades those indices coincide.
  const dateTicks = [
    ...new Set([0, Math.floor((points.length - 1) / 2), points.length - 1]),
  ];

  const nearest = (event: React.PointerEvent<SVGSVGElement>) => {
    const box = event.currentTarget.getBoundingClientRect();
    const at = event.clientX - box.left;
    let best = 0;
    for (let i = 1; i < points.length; i++) {
      if (Math.abs(x(i) - at) < Math.abs(x(best) - at)) best = i;
    }
    setHover(best);
  };

  const point = hover === null ? null : points[hover];

  return (
    <div className="equity-wrap" ref={wrap}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="equity"
        role="img"
        aria-label={`Realized equity from ${dayShort(points[0].date)} to ${dayShort(
          points[points.length - 1].date,
        )}, ending at ${rupee(last, 0)}`}
        onPointerMove={nearest}
        onPointerLeave={() => setHover(null)}
      >
        {ticks.map((value) => (
          <g key={value}>
            <line
              x1={left}
              y1={y(value)}
              x2={width - right}
              y2={y(value)}
              className={value === 0 ? "axis-zero" : "axis-grid"}
            />
            <text x={left - 8} y={y(value) + 3.5} className="axis-label" textAnchor="end">
              {rupeeShort(value)}
            </text>
          </g>
        ))}

        {dateTicks.map((i) => (
          <text
            key={i}
            x={x(i)}
            y={height - 8}
            className="axis-label"
            textAnchor={i === 0 ? "start" : i === points.length - 1 ? "end" : "middle"}
          >
            {dayShort(points[i].date)}
          </text>
        ))}

        <path
          d={`${path} L ${x(points.length - 1)} ${y(min)} L ${x(0)} ${y(min)} Z`}
          fill={stroke}
          opacity={0.08}
        />
        <path d={path} fill="none" stroke={stroke} strokeWidth={2} />
        <circle cx={x(points.length - 1)} cy={y(last)} r={3.5} fill={stroke} />

        {hover !== null && (
          <g>
            <line
              x1={x(hover)}
              y1={top}
              x2={x(hover)}
              y2={height - bottom}
              className="axis-grid"
            />
            <circle cx={x(hover)} cy={y(points[hover].equity)} r={4} fill={stroke} />
          </g>
        )}
      </svg>

      {point && (
        <div className="equity-readout">
          <strong className={signClass(point.equity)}>{rupee(point.equity, 0)}</strong>
          <span className="muted"> · {dayShort(point.date)}</span>
        </div>
      )}
    </div>
  );
}

